import React, { useEffect, useMemo } from 'react';
import UnauthenticatedRootView from '../views/unauthenticated-root-view/UnauthenticatedRootView';
import LoggedOutTemplate from '../templates/LoggedOutTemplate';
import LoggedInTemplate from '../templates/LoggedInTemplate';
import AuthenticationStateErrorView from '../views/authentication-state-error-view/AuthenticationStateErrorView';
import Prompt from '../ui/prompt/Prompt';
import TokenStateErrorView from '../views/token-state-error-view/TokenStateErrorView';
import DcsPromo from '../views/promo/DcsPromo';
import ChatViewLayout, { ChatViewLayoutProps } from '../layouts/chat-view-layout/ChatViewLayout';
import ChatView from '../views/chat-view/ChatView';
import CatalogView from '../views/catalog-view/CatalogView';
import InformationView from '../views/information-view/InformationView';
import NotFound from '../elements/not-found/NotFound';
import useLabels from '../hooks/useLabels';
import adminRoutes from '../features/dashboards/admin/Routes';
import useInfo from '../hooks/useInfo';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthenticationState, useAppInfo } from '../contexts/app-info/AppInfoContext';
import { TokenState, useAuth } from '../contexts/auth/AuthContext';
import { setupGlobalAnchorClickHandler } from '../helpers/hashRouting';
import { EventHandlerModel } from '../services/EventHandler';
import { defaultPersonaKey } from '../constants/consts';
import { useAdaptive } from '../contexts/adaptive/AdaptiveContext';
import { ToursSharedTargets } from '../features/tours/Tours';
import style from './Routing.module.scss';
import { useFileDownloadToast } from '../hooks/useFileDownloadToast';
import PromptManagementView from '../features/prompts/elements/prompt-management-view/PromptManagementView';
import PromptView from '../features/prompts/elements/prompt-view/PromptView';


type Props = {};

export enum DynamicRouteParams {
    chatId = 'chatId',
    objectId = 'objectId',
    objectType = 'objectType',
}

export type DynamicRouteProperties = {
    [DynamicRouteParams.chatId]?: string;
    [DynamicRouteParams.objectId]?: string;
    [DynamicRouteParams.objectType]?: string;
};

export enum AppRoute {
    root = '/',
    info = '/info',
    promptManagement = '/prompt-management',
    basicAssistant = `/${defaultPersonaKey}`,
    chat = '/chat',
    catalog = '/catalog',
    prompts = '/prompts',
    chatItem = `/chat/:chatId`,
    dcsPimPersonaPromo = '/dcs/promo'
}



export const getChatItemRoute = (chatId: string) => AppRoute.chatItem.replace(`:${DynamicRouteParams.chatId}`, chatId);

const Routing: React.FC<Props> = () => {
    const labels = useLabels();
    const { login, tokenState } = useAuth();
    const { authenticationState, preferences } = useAppInfo();
    const { allowedPersonaOptions: personas } = useInfo();

    const { catalogFavorites } = useAdaptive();

    const renderChatView = (props: ChatViewLayoutProps) => <ChatView {...props} />;
    const renderCatalogView = (props: ChatViewLayoutProps) => <CatalogView {...props} />;
    const renderPromptView = (props: ChatViewLayoutProps) => <PromptView {...props} />;
    const renderPromptManagementView = (props: ChatViewLayoutProps) => <PromptManagementView {...props} />;


    const renderPersonaRoutes = useMemo(() => {
        return personas.map((persona) => {
            return <Route key={persona.key} path={'/' + persona.route} element={<ChatViewLayout key='same-key' children={renderChatView} newPrompt newPromptOrigin={persona.key} />} />;
        });
    }, [personas]);

    const firstFavorite = useMemo(() => {
        let p = catalogFavorites.firstFavorite || preferences?.personas_pinned?.[0];

        if (p) {
            const persona = personas?.find(x => x.key === p);
            p = persona?.route;
        }

        return p || defaultPersonaKey;
    }, [catalogFavorites.firstFavorite, preferences?.personas_pinned, personas]);

    const renderAdminRoutes = useMemo(() => adminRoutes(), []);

    const renderAuthenticated = useMemo<React.ReactNode>(() => {
        return <div className={style['tour-init']} id={ToursSharedTargets.tourContainer}>
            <Routes>
                <Route path={AppRoute.info} element={<InformationView />} />
                <Route path={AppRoute.promptManagement} element={<ChatViewLayout children={renderPromptManagementView} />} />
                <Route path={AppRoute.root} element={<Navigate to={firstFavorite} replace={true} />} />
                <Route path={AppRoute.chat} element={<Navigate to={'/' + firstFavorite} replace={true} />} />
                <Route path={AppRoute.dcsPimPersonaPromo} element={<DcsPromo />} />
                <Route path={AppRoute.catalog} element={<ChatViewLayout children={renderCatalogView} newPrompt key='same-key' />} />
                <Route path={AppRoute.prompts} element={<ChatViewLayout children={renderPromptView} />} />

                {renderPersonaRoutes}
                {renderAdminRoutes}

                <Route path={AppRoute.chatItem + '/*'} element={<ChatViewLayout children={renderChatView} />} />
                <Route path='/*' element={<NotFound />} />
            </Routes>

            {tokenState === TokenState.RequiresInteraction && <Prompt
                className={style['token-expired-prompt']}
                isOpen={tokenState === TokenState.RequiresInteraction}
                headline={labels.tokenExpiredHeadline}
                body={labels.tokenExpiredDescription}
                closeOnBackdropClick={false}
                closeOnEscape={false}
                primaryButton={{
                    label: labels.login,
                    onClick: login,
                }}
            />}
        </div>;
    }, [firstFavorite, labels.login, labels.tokenExpiredDescription, labels.tokenExpiredHeadline, login, renderAdminRoutes, renderPersonaRoutes, tokenState]);

    const renderLoggedIn = useMemo<React.ReactNode>(() => {
        if (
            tokenState !== TokenState.Valid &&
            authenticationState !== AuthenticationState.Authenticated
        ) return <TokenStateErrorView />;
        else {
            if (authenticationState === AuthenticationState.Authenticated) return renderAuthenticated;
            else return <AuthenticationStateErrorView />;
        }
    }, [authenticationState, renderAuthenticated, tokenState]);

    const renderLoggedOut = useMemo<React.ReactNode>(() => {
        return <UnauthenticatedRootView />;
    }, []);

    return (
        <>
            <GlobalAnchorHandler />
            <LoggedInTemplate>
                {renderLoggedIn}
            </LoggedInTemplate>

            <LoggedOutTemplate>
                {renderLoggedOut}
            </LoggedOutTemplate>
        </>
    );
};

export function GlobalAnchorHandler() {
    const navigate = useNavigate();
    const { download } = useAppInfo();
    const fileDownloadToast = useFileDownloadToast();
    const localAnchorHandler = setupGlobalAnchorClickHandler();
    useEffect(() => {
        const onLocalNavigation = (event: EventHandlerModel<typeof localAnchorHandler>) => {
            if ('download' in event) {
                const downloadPromise = download(event.download);
                fileDownloadToast(downloadPromise);

            } else
                navigate({ pathname: event.path, hash: event.hash });
        };

        localAnchorHandler.on(onLocalNavigation);
        return () => {
            localAnchorHandler.off(onLocalNavigation);
        };
    }, [navigate, download, localAnchorHandler, fileDownloadToast]);

    return <></>;
}

export default Routing;
